import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía operativa del módulo de Citas (agendamiento presencial con la
 * alcaldía). Pensada para admin_tenant y funcionarios de atención
 * presencial — explica cómo se configura, cómo agenda el ciudadano y
 * cómo se opera la agenda del día.
 *
 * Si cambias los estados o los flujos, ajusta también el "duracion"
 * del índice de docs.
 */
export function DocsCitasPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Módulo de Citas
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Citas — agendamiento de atención presencial
        </h1>

        <div className="callout">
          <strong>Tiempo de configuración inicial:</strong> 20-40 min (definir
          servicios, horarios y responsables). El uso diario por funcionario
          son segundos por cita: check-in, atender, completar.
        </div>

        {/* ---- Qué es ---- */}
        <Section title="1. Qué es el módulo de Citas">
          <p>
            Permite a los <strong>ciudadanos reservar un turno</strong>{" "}
            (presencial o virtual) para un servicio puntual de la alcaldía —
            por ejemplo: pago de impuesto predial, certificado de residencia,
            asesoría de SISBÉN, audiencia con secretaría de gobierno, etc.
          </p>
          <p>
            En lugar de la fila clásica de &quot;tomar un papelito y esperar
            3 horas&quot;, el ciudadano elige el servicio + fecha + hora desde
            el portal público de la alcaldía y recibe un <strong>código de
            turno</strong> (ej. <code>A4K-7XQ</code>) por WhatsApp o email.
          </p>
          <p>
            Para el funcionario de atención presencial, el módulo se ve como
            un <strong>tablero kanban del día</strong> con las columnas:
          </p>
          <Table
            head={["Columna", "Significa"]}
            rows={[
              ["Por llegar", "Reservada / confirmada, todavía no llegó"],
              ["Llegaron", "Hizo check-in, está esperando ser atendido"],
              ["Atendiendo", "Un funcionario lo está atendiendo en este momento"],
              ["Atendidas", "Servicio completado"],
              ["Canceladas / No vino", "Cancelada o no se presentó"],
            ]}
          />
        </Section>

        {/* ---- Activación ---- */}
        <Section title="2. Activación del módulo por tenant">
          <p>
            El módulo de Citas se controla con un <strong>feature flag</strong>{" "}
            a nivel de tenant. Hasta que un super_admin de MiPueblo lo activa,
            los endpoints de citas devuelven{" "}
            <code>403 — El módulo de turnos no está habilitado para esta
            alcaldía</code>.
          </p>
          <Ol>
            <li>
              Equipo MiPueblo entra al panel super-admin →{" "}
              <Kbd>Alcaldías</Kbd> → seleccionar tenant
            </li>
            <li>
              Toggle <Kbd>Módulo de citas / turnos</Kbd> → <strong>ON</strong>
            </li>
            <li>
              El menú &quot;Citas&quot; aparece automáticamente en el sidebar
              del tenant-app para todos los funcionarios
            </li>
            <li>
              El admin_tenant ya puede entrar a configurar servicios y horarios
            </li>
          </Ol>
          <div className="callout tip">
            💡 Si la alcaldía contrató el plan que NO incluye citas, el toggle
            queda bloqueado hasta upgrade del plan.
          </div>
        </Section>

        {/* ---- Tipos de cita ---- */}
        <Section title="3. Configurar tipos de cita (servicios)">
          <p>
            Cada tipo de cita es un <strong>ServicioAlcaldia</strong>. Solo el
            rol <code>admin_tenant</code> puede crear/editar servicios. Los
            demás roles solo pueden ver el catálogo.
          </p>
          <p>
            Entrar a <Kbd>Citas</Kbd> → pestaña <Kbd>Servicios</Kbd> →{" "}
            <Kbd>Nuevo servicio</Kbd>. Campos:
          </p>
          <Table
            head={["Campo", "Qué significa"]}
            rows={[
              [
                "Código",
                "Identificador corto y único (ej. predial-pago). Solo minúsculas, números y guiones.",
              ],
                            ["Nombre", "El que ve el ciudadano (ej. Pago de Impuesto Predial)"],
              [
                "Descripción",
                "Explicación corta de qué incluye el trámite. Opcional pero recomendado.",
              ],
              [
                "Dependencia",
                "La secretaría/dependencia que atiende este servicio. Define quiénes lo gestionan y de qué horario hereda.",
              ],
              [
                "Duración (min)",
                "Cuánto dura cada cita. Define el tamaño del slot. Entre 5 y 480 min.",
              ],
              [
                "Capacidad simultánea",
                "Cuántas personas pueden ser atendidas al mismo tiempo (ej. 3 ventanillas en paralelo = 3).",
              ],
              [
                "Documentos requeridos",
                "Lista de documentos que el ciudadano debe traer. Aparecen en la confirmación.",
              ],
              [
                "Modalidad",
                "Presencial / Virtual / Ambas. Si es virtual, la confirmación incluye link.",
              ],
              ["Activo", "Si está OFF, no aparece en el portal público"],
              [
                "Orden",
                "Para reordenar manualmente la lista que ve el ciudadano (más bajo = primero).",
              ],
            ]}
          />
          <div className="callout warn">
            ⚠️ Si un servicio ya tiene citas históricas, al eliminarlo solo se
            <strong> desactiva</strong> (no se borra) — para preservar los
            registros de auditoría.
          </div>
        </Section>

        {/* ---- Horarios ---- */}
        <Section title="4. Configurar horarios de atención">
          <p>
            Los slots de citas <strong>NO se persisten</strong> en la base de
            datos: se calculan dinámicamente cada vez que el ciudadano consulta
            la disponibilidad, en base a tres cosas:
          </p>
          <Ul>
            <li>
              <strong>Horario de la dependencia</strong> dueña del servicio
              (si tiene horario propio configurado)
            </li>
            <li>
              <strong>Horario del tenant</strong> como fallback (si la
              dependencia no define el suyo)
            </li>
            <li>
              <strong>Festivos colombianos</strong> (los días de la tabla de
              festivos no generan slots)
            </li>
          </Ul>
          <p>
            <strong>Menos</strong> las citas ya reservadas que ocupen ese slot
            (excepto canceladas y no_show, que liberan el cupo).
          </p>
          <p>
            Esto significa que si cambias el horario de la dependencia un
            viernes a las 5pm, el efecto es <strong>inmediato</strong> — no
            hay que regenerar nada.
          </p>
          <Ol>
            <li>
              Ir a <Kbd>Dependencias</Kbd> → seleccionar la dependencia →
              pestaña <Kbd>Horario</Kbd>
            </li>
            <li>
              Para cada día de la semana, marcar <Kbd>Abierto</Kbd> o{" "}
              <Kbd>Cerrado</Kbd>
            </li>
            <li>
              Para los días abiertos, definir uno o varios{" "}
              <strong>tramos</strong> (ej. 8:00–12:00 y 14:00–17:00 para
              jornada partida)
            </li>
            <li>
              La zona horaria por defecto es <code>America/Bogota</code> — no
              hay que tocarla
            </li>
            <li>Guardar</li>
          </Ol>
          <div className="callout tip">
            💡 La duración del servicio divide cada tramo. Si el tramo es
            8:00–12:00 y el servicio dura 30 min, el ciudadano verá slots:
            8:00, 8:30, 9:00, ... hasta 11:30.
          </div>
        </Section>

        {/* ---- Funcionarios ---- */}
        <Section title="5. Funcionarios que pueden atender citas">
          <p>
            No hay un &quot;rol de citas&quot; aparte — cualquier funcionario
            del tenant que esté asignado a la dependencia dueña del servicio
            puede operar sus citas. La visibilidad se reduce automáticamente
            según el rol:
          </p>
          <Table
            head={["Rol", "Qué citas ve en su tablero"]}
            rows={[
              ["admin_tenant", "Todas las citas del tenant"],
              ["supervisor", "Todas las citas del tenant"],
              [
                "funcionario",
                "Solo las citas de servicios cuya dependencia esté en la sub-rama de SUS dependencias asignadas (principal + secundarias N:N)",
              ],
              [
                "funcionario sin dependencia",
                "Ve todas (caso transversal — útil para atención en ventanilla única)",
              ],
            ]}
          />
          <p>
            Para asignar dependencias a un funcionario: <Kbd>Usuarios</Kbd> →
            seleccionar usuario → <Kbd>Dependencias</Kbd>.
          </p>
        </Section>

        {/* ---- Flujo ciudadano ---- */}
        <Section title="6. Flujo del ciudadano (cómo agenda)">
          <Ol>
            <li>
              Entra al portal público de la alcaldía (ej.{" "}
              <code>cajica.mipuebloapp.com</code>) → <Kbd>Pedir cita</Kbd>
            </li>
            <li>
              Ve el catálogo de servicios activos con duración, modalidad y
              documentos requeridos
            </li>
            <li>Elige el servicio → elige fecha (calendario) → elige hora</li>
            <li>
              Llena sus datos (nombre, documento, teléfono, email) y, si
              quiere, una nota corta para el funcionario
            </li>
            <li>
              Confirma. La cita queda en estado <code>reservada</code> con un{" "}
              <strong>código de turno único</strong>
            </li>
            <li>
              Recibe inmediatamente un mensaje de confirmación por <strong>
              WhatsApp</strong> (si dejó teléfono y el tenant tiene WhatsApp
              configurado) o por <strong>email</strong> como fallback
            </li>
            <li>
              24h antes le llega <strong>recordatorio automático</strong> por
              el mismo canal
            </li>
            <li>
              Puede consultar o cancelar la cita en cualquier momento
              ingresando su código de turno en el portal
            </li>
          </Ol>
          <div className="callout warn">
            ⚠️ Cancelación por ciudadano: solo permitida hasta{" "}
            <strong>1 hora antes</strong> del inicio. Después tiene que
            llamar a la alcaldía.
          </div>
        </Section>

        {/* ---- Flujo funcionario ---- */}
        <Section title="7. Flujo del funcionario (operación diaria)">
          <p>
            El funcionario entra cada mañana a <Kbd>Citas</Kbd> en el tenant-app
            y ve el tablero del día. Puede cambiar de fecha o filtrar por
            servicio. Cada tarjeta de cita tiene botones contextuales según
            el estado.
          </p>
          <h4 style={{ marginTop: 16, marginBottom: 8 }}>
            Estados y transiciones permitidas
          </h4>
          <Table
            head={["Acción", "Desde estado", "Pasa a estado"]}
            rows={[
              ["Check-in (ciudadano llegó)", "reservada / confirmada", "llego"],
              ["Atender (comenzar)", "llego / confirmada", "atendiendo"],
              ["Completar (terminó)", "atendiendo / llego", "atendida"],
              ["Marcar no vino", "reservada / confirmada", "no_show"],
              [
                "Cancelar (con motivo obligatorio)",
                "cualquier estado activo",
                "cancelada_alcaldia",
              ],
              ["Editar notas internas", "cualquiera", "(no cambia estado)"],
            ]}
          />
          <p>
            Cuando un funcionario marca <Kbd>Atender</Kbd>, queda registrado
            como el responsable de esa cita (<code>atendido_por_id</code>) —
            esto alimenta los reportes de ocupación por persona.
          </p>
          <div className="callout tip">
            💡 Las <strong>notas internas del funcionario</strong> son
            privadas — nunca se le muestran al ciudadano. Las notas del
            ciudadano sí son visibles para el funcionario en la tarjeta.
          </div>
        </Section>

        {/* ---- Confirmaciones ---- */}
        <Section title="8. Confirmaciones automáticas">
          <p>
            Apenas el ciudadano confirma la reserva, MiPueblo le manda un
            mensaje. El canal se elige así (best-effort, sin bloquear la
            creación de la cita):
          </p>
          <Ol>
            <li>
              <strong>WhatsApp</strong> — si el ciudadano dejó teléfono y el
              tenant tiene WhatsApp Cloud API configurado
            </li>
            <li>
              <strong>Email</strong> — fallback si WhatsApp falló o no
              aplica
            </li>
            <li>
              Si ninguno funciona, la cita igual queda creada — el ciudadano
              ve el código en pantalla
            </li>
          </Ol>
          <p>El mensaje incluye:</p>
          <Ul>
            <li>Fecha y hora (en español, formato familiar)</li>
            <li>Duración estimada</li>
            <li>Nombre del servicio</li>
            <li>Código de turno (ej. A4K-7XQ)</li>
            <li>Recordatorio de llegar 10 minutos antes</li>
            <li>Instrucción para cancelar desde el portal</li>
          </Ul>
        </Section>

        {/* ---- Recordatorios ---- */}
        <Section title="9. Recordatorios automáticos 24h antes">
          <p>
            Un cron del worker corre periódicamente y busca todas las citas
            activas (<code>reservada</code> o <code>confirmada</code>) cuyo
            inicio caiga en la ventana <strong>[ahora + 23h, ahora +
            25h]</strong>. A cada una le manda un recordatorio por el mismo
            canal que la confirmación (WhatsApp con fallback a email).
          </p>
          <p>
            El envío es <strong>idempotente</strong>: una vez enviado, la
            cita queda marcada internamente y no se vuelve a notificar
            aunque el cron corra varias veces.
          </p>
          <div className="callout tip">
            💡 Si la alcaldía aún no tiene WhatsApp activado, los
            recordatorios salen por email. Recomendamos activar WhatsApp
            (ver guía de <Link to="/docs/whatsapp">WhatsApp Cloud API</Link>)
            porque la tasa de lectura es 10x mayor.
          </div>
        </Section>

        {/* ---- Reportes ---- */}
        <Section title="10. Reportes y métricas">
          <p>
            El endpoint <Kbd>resumen/dia</Kbd> devuelve los conteos por
            estado para una fecha. Útil para el dashboard de supervisión:
          </p>
          <Table
            head={["Métrica", "Cómo se calcula"]}
            rows={[
              ["Total de citas del día", "Suma de todos los estados"],
              [
                "Atendidas",
                "Estado = atendida — alimenta tasa de cumplimiento",
              ],
              [
                "No-shows",
                "Estado = no_show — alta tasa indica que faltan recordatorios o que cobrar señal",
              ],
              [
                "Canceladas por ciudadano",
                "Estado = cancelada_ciudadano — sano si es bajo (5–15%)",
              ],
              [
                "Canceladas por alcaldía",
                "Estado = cancelada_alcaldia — debería ser excepcional",
              ],
              [
                "Ocupación por funcionario",
                "Agrupar atendidas por atendido_por_id",
              ],
            ]}
          />
          <p>
            La tabla de auditoría registra cada transición (check-in,
            atender, completar, cancelar, no-show) con actor, timestamp y
            motivo cuando aplique — útil para investigar reclamos.
          </p>
        </Section>

        {/* ---- Cancelación ---- */}
        <Section title="11. Cancelación y reagendamiento">
          <p>
            <strong>Política actual</strong>: no hay reagendamiento directo.
            Si el ciudadano necesita cambiar de hora, debe cancelar la cita
            actual y crear una nueva. Esto mantiene la trazabilidad limpia
            (cada slot ocupado/liberado queda en histórico).
          </p>
          <Table
            head={["Quién cancela", "Cuándo puede", "Estado final"]}
            rows={[
              [
                "Ciudadano (desde portal)",
                "Hasta 1 hora antes del inicio",
                "cancelada_ciudadano",
              ],
              [
                "Funcionario",
                "En cualquier momento, requiere motivo (≥2 caracteres)",
                "cancelada_alcaldia",
              ],
              [
                "Sistema (no aplica)",
                "Nunca cancela solo — el no-show es marcado por el funcionario",
                "—",
              ],
            ]}
          />
          <p>
            Una cita cancelada o marcada como no-show <strong>libera
            inmediatamente</strong> el slot — otro ciudadano puede reservarlo
            en cuanto refresque la lista de disponibilidad.
          </p>
        </Section>

        {/* ---- Integración ---- */}
        <Section title="12. Integración con dependencias y horarios">
          <p>
            El módulo de Citas se apoya en estos tres módulos del sistema:
          </p>
          <Table
            head={["Módulo", "Para qué lo usa Citas"]}
            rows={[
              [
                "Dependencias",
                "Cada servicio pertenece a una dependencia. Define qué funcionarios lo ven y de dónde hereda el horario.",
              ],
              [
                "Horarios",
                "El cálculo de slots lee el horario semanal de la dependencia (con fallback al tenant).",
              ],
              [
                "Festivos CO",
                "Tabla compartida de festivos colombianos. Esos días no generan slots automáticamente.",
              ],
              [
                "WhatsApp / Email",
                "Canales de salida para confirmaciones y recordatorios.",
              ],
              [
                "Peticionarios",
                "Cada cita queda asociada al peticionario que la reservó (mismo registro que usa PQRSDF).",
              ],
              [
                "Auditoría",
                "Cada transición de estado deja rastro con actor, timestamp y motivo.",
              ],
            ]}
          />
        </Section>

        {/* ---- Buenas prácticas ---- */}
        <Section title="13. Buenas prácticas">
          <Checklist
            items={[
              <>
                <strong>Empieza con pocos servicios</strong> (3–5) y agrega
                más cuando el equipo ya está cómodo con el flujo. Es más
                fácil agregar que quitar.
              </>,
              <>
                <strong>Duraciones realistas</strong> — incluye el tiempo de
                papeleo, no solo la atención pura. Si la cita demora 20 min
                pero el funcionario tarda 10 min más cerrando, configura 30.
              </>,
              <>
                <strong>Capacidad simultánea = número real de
                ventanillas</strong>. Si pones 1 y tienes 3 ventanillas,
                desperdicias 2/3 de la capacidad. Si pones 3 y solo hay 1,
                vas a tener gente esperando.
              </>,
              <>
                <strong>Lista bien los documentos requeridos</strong> — cada
                ciudadano que llega sin papeles es un no-show disfrazado.
              </>,
              <>
                <strong>Activa WhatsApp</strong> antes que email — la tasa
                de no-show baja mucho cuando los recordatorios llegan por
                WhatsApp.
              </>,
              <>
                <strong>Revisa el tablero al inicio del día</strong> y haz
                check-in apenas el ciudadano llegue. Eso da datos limpios
                de tiempo de espera.
              </>,
              <>
                <strong>No abuses de la cancelación por alcaldía</strong> —
                cada vez que cancela la alcaldía, el ciudadano pierde
                confianza. Reserva ese estado para casos reales (funcionario
                enfermo, emergencia).
              </>,
              <>
                <strong>Audita mensualmente</strong> la tasa de no-show por
                servicio. Si un servicio tiene &gt;25%, revisa duración,
                horarios o claridad de los documentos.
              </>,
              <>
                <strong>No mezcles servicios con duraciones muy distintas
                en la misma dependencia</strong> sin haber pensado el
                horario — si una dependencia tiene un servicio de 15 min y
                otro de 2h, el de 2h tapa el calendario.
              </>,
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ children }: { children: React.ReactNode }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13.5,
        }}
      >
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{
                borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)",
              }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Re-exports para evitar warnings de "unused" si el linter es estricto
// y para que estén disponibles si se añaden enlaces externos más adelante.
export { ExtLink };
